import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Menu, Plus, Lock, ShieldCheck, X, Settings as SettingsIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import {
  NAV_PRIMARY_MOBILE as PRIMARY,
  NAV_SECONDARY_MOBILE as SECONDARY,
  ADMIN_ITEMS as _ADMIN_ITEMS,
} from "@/config/nav.config";

// Rewire admin item testids for the mobile "More" sheet.
const ADMIN_ITEMS = _ADMIN_ITEMS.map((a) => ({
  ...a,
  testId: `bottomnav-admin-${a.to.split("/").pop()}`,
}));

/**
 * MobileBottomNav — native-app-style bottom bar for <lg viewports.
 *
 * Layout: [Dashboard] [Expenses] [+ FAB] [Reports] [More →]
 *  - FAB navigates to /expenses with `state.autoOpenAdd` so the Add
 *    modal opens on landing (no extra tap for the primary action).
 *  - "More" opens a sheet with the full secondary nav + admin quicknav.
 *  - `proOnly` items in the More sheet render as greyed-out buttons
 *    that fire the upgrade prompt (via `onProLocked`).
 *
 * Rendered by AppLayout only when the sidebar is hidden (below `lg`).
 * The parent must add `pb-24` to main content so the fixed bar
 * doesn't cover the last row.
 */

export default function MobileBottomNav({ hasFullAccess, isAdmin, onProLocked }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleFabClick = () => {
    // Best-effort haptic feedback on tap. Supported on Android + some
    // iOS versions; silently no-op otherwise.
    try { navigator.vibrate?.(12); } catch (err) { /* no haptics */ }
    // Navigate to /expenses and signal Expenses.jsx to auto-open the
    // add modal. If already on /expenses, just push new state so the
    // useEffect there re-triggers.
    navigate("/expenses", { state: { autoOpenAdd: Date.now() } });
  };

  const goSecondary = (item) => {
    setSheetOpen(false);
    if (item.proOnly && !hasFullAccess) {
      onProLocked?.(item.i18nKey);
      return;
    }
    navigate(item.to);
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[var(--border-default)] bg-[var(--bg-primary)]/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-end justify-around px-2 pt-1.5 pb-2">
        {/* Left two items */}
        {PRIMARY.slice(0, 2).map((item) => (
          <BottomTab key={item.to} item={item} hasFullAccess={hasFullAccess} onProLocked={onProLocked} />
        ))}

        {/* Center FAB — elevated, primary action */}
        <button
          type="button"
          onClick={handleFabClick}
          aria-label={t("bottomnav.add", { defaultValue: "Add expense" })}
          data-testid="bottomnav-fab"
          className="relative -mt-6 h-14 w-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          style={{
            background: "linear-gradient(135deg, var(--accent-primary) 0%, #2D5A40 100%)",
            boxShadow: "0 8px 20px -4px rgba(45,90,64,0.35)",
          }}
        >
          <Plus size={26} strokeWidth={2.4} className="text-white" />
        </button>

        {/* Right item */}
        <BottomTab item={PRIMARY[2]} hasFullAccess={hasFullAccess} onProLocked={onProLocked} />

        {/* More button — opens sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="More"
              data-testid="bottomnav-more"
              className={`flex flex-col items-center justify-center gap-0.5 h-14 min-w-[56px] rounded-lg text-[10px] font-medium transition-colors ${
                location.pathname.startsWith("/admin") || location.pathname === "/settings"
                  ? "text-[var(--accent-primary)]"
                  : "text-[var(--text-secondary)]"
              }`}
            >
              <Menu size={22} strokeWidth={2} />
              <span>More</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[80vh] overflow-y-auto rounded-t-2xl p-0"
            data-testid="bottomnav-more-sheet"
          >
            <SwipeCloseHeader onClose={() => setSheetOpen(false)} title={t("bottomnav.more", { defaultValue: "All sections" })} />

            {isAdmin && (
              <div className="px-5 py-4 border-b border-[var(--border-default)]" data-testid="bottomnav-admin-section">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={13} style={{ color: "#E09E50" }} />
                  <div className="label-eyebrow" style={{ color: "#E09E50" }}>Admin console</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {ADMIN_ITEMS.map((a) => (
                    <button
                      key={a.to}
                      data-testid={a.testId}
                      onClick={() => { setSheetOpen(false); navigate(a.to); }}
                      className="text-left px-3 py-2.5 rounded-lg border border-[var(--border-default)] hover:border-[#E09E50] hover:text-[#E09E50] text-sm transition-colors"
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 py-4">
              <div className="label-eyebrow mb-3">{t("bottomnav.sections", { defaultValue: "Sections" })}</div>
              <div className="grid grid-cols-2 gap-2">
                {SECONDARY.map((item) => {
                  const Icon = item.icon;
                  const gated = item.proOnly && !hasFullAccess;
                  return (
                    <button
                      key={item.to}
                      data-testid={`bottomnav-more-${item.i18nKey}`}
                      onClick={() => goSecondary(item)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-[var(--border-default)] text-sm transition-colors ${
                        gated
                          ? "opacity-60 hover:opacity-90"
                          : "hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]"
                      }`}
                    >
                      <Icon size={16} className="shrink-0" />
                      <span className="flex-1 text-left truncate">{t(`nav.${item.i18nKey}`)}</span>
                      {gated && <Lock size={11} className="text-[var(--text-muted)]" />}
                    </button>
                  );
                })}
                <button
                  data-testid="bottomnav-more-settings"
                  onClick={() => { setSheetOpen(false); navigate("/settings"); }}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-[var(--border-default)] text-sm hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-colors"
                >
                  <SettingsIcon size={16} className="shrink-0" />
                  <span className="flex-1 text-left">{t("nav.settings", { defaultValue: "Settings" })}</span>
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}

function BottomTab({ item, hasFullAccess, onProLocked }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const gated = item.proOnly && !hasFullAccess;

  if (gated) {
    return (
      <button
        type="button"
        onClick={() => onProLocked?.(item.i18nKey)}
        data-testid={item.testId}
        className="flex flex-col items-center justify-center gap-0.5 h-14 min-w-[56px] rounded-lg text-[10px] font-medium text-[var(--text-muted)] opacity-70 relative"
      >
        <Icon size={22} strokeWidth={2} />
        <span>{t(`nav.${item.i18nKey}`)}</span>
        <Lock size={9} className="absolute top-1 right-2 text-[var(--text-muted)]" />
      </button>
    );
  }

  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 h-14 min-w-[56px] rounded-lg text-[10px] font-medium transition-colors ${
          isActive
            ? "text-[var(--accent-primary)]"
            : "text-[var(--text-secondary)] hover:text-[var(--accent-primary)]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={22} strokeWidth={isActive ? 2.4 : 2} />
          <span>{t(`nav.${item.i18nKey}`)}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * SwipeCloseHeader — sticky top handle for the mobile "More" sheet.
 * - Visible drag handle (bar) + title + explicit close button.
 * - Touch handlers implement pull-down-to-close: dragging past
 *   ~80px downward closes the sheet. Under threshold snaps back.
 * - Only tracks single-touch downward gestures so it doesn't fight
 *   the sheet's internal scroll on horizontal/upward moves.
 */
function SwipeCloseHeader({ onClose, title }) {
  const startY = useRef(null);
  const [dragY, setDragY] = useState(0);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    startY.current = e.touches[0].clientY;
    setDragY(0);
  };

  const onTouchMove = (e) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    // Only track downward drags — upward should let content scroll.
    if (dy > 0) setDragY(dy);
  };

  const onTouchEnd = () => {
    if (startY.current == null) return;
    if (dragY > 80) {
      onClose?.();
    }
    startY.current = null;
    setDragY(0);
  };

  return (
    <>
      <SheetTitle className="sr-only">{title}</SheetTitle>
      <SheetDescription className="sr-only">
        Bottom navigation menu with quick links to all sections and admin console.
      </SheetDescription>
      <div
        data-testid="bottomnav-swipe-header"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        className="sticky top-0 z-10 bg-[var(--bg-primary)] border-b border-[var(--border-default)] cursor-grab active:cursor-grabbing select-none"
        style={{
          transform: dragY ? `translateY(${Math.min(dragY, 120)}px)` : undefined,
          transition: dragY ? "none" : "transform 180ms ease-out",
        }}
      >
        <div className="flex justify-center pt-2.5 pb-1.5">
          <div
            data-testid="bottomnav-swipe-handle"
            className="h-1 w-10 rounded-full bg-[var(--border-default)]"
          />
        </div>
        <div className="flex items-center justify-between px-5 pb-3">
          <div className="text-sm font-medium text-[var(--text-primary)]">{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="bottomnav-swipe-close"
            className="h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-accent)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
}

